/**
 * Formatea un valor numérico al formato español de precios.
 * - Separador de miles: punto (.)
 * - Separador decimal: coma (,)
 * - Máximo 6 decimales
 * 
 * @param {string|number} value - El valor a formatear
 * @returns {string} El valor formateado en formato español
 * 
 * @example
 * formatSpanishPrice("1000") // "1.000"
 * formatSpanishPrice("1234567") // "1.234.567"
 * formatSpanishPrice("1234567,89") // "1.234.567,89"
 * formatSpanishPrice("500,123456") // "500,123456"
 */
export const formatSpanishPrice = (value) => {
  if (!value && value !== 0) return "";
  
  const stringValue = value.toString();
  
  // Eliminar todo excepto números y coma
  let cleanValue = stringValue.replace(/[^0-9,]/g, "");
  
  // Asegurar que solo haya una coma
  const parts = cleanValue.split(",");
  if (parts.length > 2) {
    cleanValue = parts[0] + "," + parts.slice(1).join("");
  }
  
  // Separar parte entera y decimal
  const [integerPart, decimalPart] = cleanValue.split(",");
  
  // Formatear parte entera con separadores de miles
  let formattedInteger = integerPart;
  if (integerPart && integerPart.length > 0) {
    formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }
  
  // Reconstruir el valor
  let formattedValue = formattedInteger;
  if (decimalPart !== undefined) {
    // Limitar decimales a 6 dígitos
    formattedValue = formattedInteger + "," + decimalPart.substring(0, 6);
  }
  
  return formattedValue;
};

/**
 * Convierte un valor en formato español a formato numérico estándar (para enviar al backend).
 * - Elimina puntos (separadores de miles)
 * - Convierte coma decimal a punto
 * 
 * @param {string} spanishValue - El valor en formato español
 * @returns {number} El valor numérico
 * 
 * @example
 * parseSpanishPrice("1.234.567,89") // 1234567.89
 * parseSpanishPrice("1.000") // 1000
 * parseSpanishPrice("500,5") // 500.5
 */
export const parseSpanishPrice = (spanishValue) => {
  if (!spanishValue && spanishValue !== 0) return null;
  
  const stringValue = spanishValue.toString();
  
  // Eliminar puntos (separadores de miles) y convertir coma a punto
  const numericValue = stringValue.replace(/\./g, "").replace(",", ".");
  
  return parseFloat(numericValue);
};

/**
 * Formatea un número con separadores de miles usando punto y opcionalmente decimales con coma.
 * Similar a formatSpanishPrice pero sin límite de decimales.
 * 
 * @param {string|number} value - El valor a formatear
 * @param {number} maxDecimals - Número máximo de decimales (por defecto sin límite)
 * @returns {string} El valor formateado
 * 
 * @example
 * formatNumberWithThousands("1234567") // "1.234.567"
 * formatNumberWithThousands("1234.5678", 2) // "1.234,57"
 */
export const formatNumberWithThousands = (value, maxDecimals = null) => {
  if (!value && value !== 0) return "";
  
  const stringValue = value.toString();
  
  // Eliminar todo excepto números y coma/punto
  let cleanValue = stringValue.replace(/[^0-9,.]/g, "");
  
  // Convertir punto a coma si existe
  cleanValue = cleanValue.replace(".", ",");
  
  // Asegurar que solo haya una coma
  const parts = cleanValue.split(",");
  if (parts.length > 2) {
    cleanValue = parts[0] + "," + parts.slice(1).join("");
  }
  
  // Separar parte entera y decimal
  const [integerPart, decimalPart] = cleanValue.split(",");
  
  // Formatear parte entera con separadores de miles
  let formattedInteger = integerPart;
  if (integerPart && integerPart.length > 0) {
    formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }
  
  // Reconstruir el valor
  let formattedValue = formattedInteger;
  if (decimalPart !== undefined) {
    const limitedDecimals = maxDecimals !== null 
      ? decimalPart.substring(0, maxDecimals)
      : decimalPart;
    formattedValue = formattedInteger + "," + limitedDecimals;
  }
  
  return formattedValue;
};

/**
 * Limpia un valor permitiendo solo números y opcionalmente coma decimal.
 * 
 * @param {string|number} value - El valor a limpiar
 * @param {boolean} allowDecimal - Si permite coma decimal (por defecto true)
 * @returns {string} El valor limpio
 * 
 * @example
 * cleanNumericValue("abc123def") // "123"
 * cleanNumericValue("123,45abc") // "123,45"
 * cleanNumericValue("123,45", false) // "12345"
 */
export const cleanNumericValue = (value, allowDecimal = true) => {
  if (!value && value !== 0) return "";
  
  const stringValue = value.toString();
  
  if (allowDecimal) {
    // Eliminar todo excepto números y coma
    let cleanValue = stringValue.replace(/[^0-9,]/g, "");
    
    // Asegurar que solo haya una coma
    const parts = cleanValue.split(",");
    if (parts.length > 2) {
      cleanValue = parts[0] + "," + parts.slice(1).join("");
    }
    
    return cleanValue;
  } else {
    // Solo números
    return stringValue.replace(/[^0-9]/g, "");
  }
};
